import { ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect } from "../config-setting-select";
import { ConfigParamPaymentSettings } from "./app-settings-config-param-payment";
import { ConfigStripeSettings } from "./app-settings-config-stripe";
import { ConfigIyzicoSettings } from "./app-settings.config-iyzico";

export interface IConfigPaymentInputState {
  showStripeModal: boolean;
  showIyzicoModal: boolean;
  showParamModal: boolean;
  editingStripeId: string;
  editingIyzicoId: string;
  editingParamId: string;
}

export interface IConfigPaymentInputProps {
  parentConfigStripeValue?: string | null;
  parentConfigIyzicoValue?: string | null;
  parentConfigParamValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigPaymentInput extends PureComponent<IConfigPaymentInputProps, IConfigPaymentInputState> {
  stripeFormInputName = ["apiConfig", "stripeSettingsId"];

  iyzicoFormInputName = ["apiConfig", "iyzicoSettingsId"];

  paramFormInputName = ["apiConfig", "paramPaymentSettingsId"];

  constructor(props: IConfigPaymentInputProps) {
    super(props);
    this.state = {
      showStripeModal: false,
      editingStripeId: "",
      showIyzicoModal: false,
      editingIyzicoId: "",
      showParamModal: false,
      editingParamId: ""
    };
  }

  openStripeModal = () => {
    this.setState({ editingStripeId: "", showStripeModal: true });
  };

  closeStripeModal = () => {
    this.setState({ showStripeModal: false });
  };

  setEditingStripe = (id: string) => {
    this.setState({ editingStripeId: id, showStripeModal: true });
  };

  openIyzicoModal = () => {
    this.setState({ editingIyzicoId: "", showIyzicoModal: true });
  };

  closeIyzicoModal = () => {
    this.setState({ showIyzicoModal: false });
  };

  setEditingIyzico = (id: string) => {
    this.setState({ editingIyzicoId: id, showIyzicoModal: true });
  };

  openParamModal = () => {
    this.setState({ editingParamId: "", showParamModal: true });
  };

  closParamoModal = () => {
    this.setState({ showParamModal: false });
  };

  setEditingParam = (id: string) => {
    this.setState({ editingParamId: id, showParamModal: true });
  };

  restore = (formInputName: string[], parentValue: string | null | undefined): void => {
    this.props.handleUpdateCustomFormInput?.(formInputName, parentValue ?? null);
  };

  render() {
    const {
      parentConfigStripeValue,
      parentConfigIyzicoValue,
      parentConfigParamValue,
      commonSettings,
      getSectionDescription,
      handleUpdateCustomFormInput,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;

    return (
      <>
        <ConfigStripeSettings
          formInputName={this.stripeFormInputName}
          closeModal={this.closeStripeModal}
          showModal={this.state.showStripeModal}
          editingStripeId={this.state.editingStripeId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigIyzicoSettings
          formInputName={this.iyzicoFormInputName}
          closeModal={this.closeIyzicoModal}
          showModal={this.state.showIyzicoModal}
          editingIyzicoId={this.state.editingIyzicoId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigParamPaymentSettings
          formInputName={this.paramFormInputName}
          closeModal={this.closParamoModal}
          showModal={this.state.showParamModal}
          editingParamPaymentId={this.state.editingParamId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigSettingSelect
          label="Stripe"
          formInputName={this.stripeFormInputName}
          options={commonSettings.stripeSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_paymentmethod_configuration"
          onAddNew={this.openStripeModal}
          onEdit={this.setEditingStripe}
          parentConfigValue={parentConfigStripeValue}
          onRestore={() => this.restore(this.stripeFormInputName, parentConfigStripeValue)}
          dropdownTestId="appSettingsStripeDropdown"
          addNewTestId="appSettingsStripeAddNewButton"
          editTestId="appSettingsStripeEditButton"
        />

        <ConfigSettingSelect
          label="Iyzico"
          formInputName={this.iyzicoFormInputName}
          options={commonSettings.iyzicoSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_paymentmethod_iyzicoconfiguration"
          onAddNew={this.openIyzicoModal}
          onEdit={this.setEditingIyzico}
          parentConfigValue={parentConfigIyzicoValue}
          onRestore={() => this.restore(this.iyzicoFormInputName, parentConfigIyzicoValue)}
          dropdownTestId="appSettingsIyzicoDropdown"
          addNewTestId="appSettingsIyzicoAddNew"
          editTestId="appSettingsIyzicoEditButton"
        />

        <ConfigSettingSelect
          label="Param"
          formInputName={this.paramFormInputName}
          options={commonSettings.paramPaymentSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_paymentmethod_param"
          onAddNew={this.openParamModal}
          onEdit={this.setEditingParam}
          parentConfigValue={parentConfigParamValue}
          onRestore={() => this.restore(this.paramFormInputName, parentConfigParamValue)}
          dropdownTestId="appSettingsParamDropdown"
          addNewTestId="appSettingsParamAddNewButton"
          editTestId="appSettingsParamEditButton"
        />
      </>
    );
  }
}
